import { all, call, put, takeLatest } from 'redux-saga/effects';
import { RequestQueryBuilder } from '@nestjsx/crud-request';
import {
  CREATE_RETAIL_PROFILE,
  GET_RETAIL_PROFILES,
  GET_RETAIL_PROFILE_BY_ID,
  GET_RETAIL_PROFILES_BY_VENUE,
  UPDATE_RETAIL_PROFILE,
  DELETE_RETAIL_PROFILE,
  CREATE_RETAIL_SCHEDULE,
  GET_SCHEDULES_BY_PROFILE,
  GET_SCHEDULES_BY_DAY,
  UPDATE_RETAIL_SCHEDULE,
  DELETE_RETAIL_SCHEDULE,
} from './constants';
import Api from '../../commons/api';
import * as RetailProfileActions from './action';
import { onDisplayAlert } from '../../utils/alerts/alerts';

const controller = 'retail-profiles';

const baseQueryBuilder = () => RequestQueryBuilder.create();

// Retail Profile Sagas
function* fetchCreateRetailProfile({ payload }) {
  const { profileData, navigate } = payload;
  try {
    const response = yield Api.post(`${controller}`, profileData);
    // Backend returns { message, status, data: profile }
    const profile = response.data.data || response.data;
    yield put(RetailProfileActions.createRetailProfileSuccess(profile));

    onDisplayAlert({
      type: 'success',
      title: 'Success',
      description: 'Retail profile created successfully!',
    });

    if (navigate) {
      navigate();
    }
  } catch (err) {
    yield put(
      RetailProfileActions.createRetailProfileFailed(err.message || 'Failed to create profile'),
    );
    onDisplayAlert({
      type: 'error',
      title: 'Error',
      description: err.response?.data?.message || 'Failed to create retail profile',
    });
  }
}

function* fetchGetRetailProfiles() {
  try {
    const query = baseQueryBuilder()
      .setJoin({ field: 'venue' })
      .setJoin({ field: 'venue.city' })
      .setJoin({ field: 'venue.state' })
      .setJoin({ field: 'schedules' })
      .query();
    const { data } = yield Api.get(`${controller}?${query}`);
    yield put(RetailProfileActions.getRetailProfilesSuccess(data));
  } catch (err) {
    yield put(
      RetailProfileActions.getRetailProfilesFailed(err.message || 'Failed to fetch profiles'),
    );
  }
}

function* fetchGetRetailProfileById({ payload }) {
  try {
    const query = baseQueryBuilder()
      .setJoin({ field: 'venue' })
      .setJoin({ field: 'venue.city' })
      .setJoin({ field: 'venue.state' })
      .setJoin({ field: 'schedules' })
      .query();
    const { data } = yield Api.get(`${controller}/${payload.id}?${query}`);
    // console.log('🔍 Fetched retail profile:', data);
    // console.log('🔍 Profile schedules:', data?.schedules);
    yield put(RetailProfileActions.getRetailProfileByIdSuccess(data));
  } catch (err) {
    console.error('❌ Error fetching retail profile:', err);
    yield put(
      RetailProfileActions.getRetailProfileByIdFailed(err.message || 'Failed to fetch profile'),
    );
  }
}

function* fetchGetRetailProfilesByVenue({ payload }) {
  try {
    const { data } = yield Api.get(`${controller}/venue/${payload.venueId}`);
    yield put(RetailProfileActions.getRetailProfilesByVenueSuccess(data));
  } catch (err) {
    yield put(
      RetailProfileActions.getRetailProfilesByVenueFailed(
        err.message || 'Failed to fetch profiles',
      ),
    );
  }
}

function* fetchUpdateRetailProfile({ payload }) {
  const { id, profileData } = payload;
  try {
    const { data } = yield Api.put(`${controller}/${id}`, profileData);
    yield put(RetailProfileActions.updateRetailProfileSuccess(data));

    onDisplayAlert({
      type: 'success',
      title: 'Success',
      description: 'Retail profile updated successfully!',
    });
  } catch (err) {
    yield put(
      RetailProfileActions.updateRetailProfileFailed(err.message || 'Failed to update profile'),
    );
    onDisplayAlert({
      type: 'error',
      title: 'Error',
      description: err.response?.data?.message || 'Failed to update retail profile',
    });
  }
}

function* fetchDeleteRetailProfile({ payload }) {
  try {
    yield Api.delete(`${controller}/${payload.id}`);
    yield put(RetailProfileActions.deleteRetailProfileSuccess(payload.id));

    onDisplayAlert({
      type: 'success',
      title: 'Success',
      description: 'Retail profile deleted successfully!',
    });
  } catch (err) {
    yield put(
      RetailProfileActions.deleteRetailProfileFailed(err.message || 'Failed to delete profile'),
    );
    onDisplayAlert({
      type: 'error',
      title: 'Error',
      description: err.response?.data?.message || 'Failed to delete retail profile',
    });
  }
}

// Retail Schedule Sagas
function* fetchCreateRetailSchedule({ payload }) {
  const { scheduleData } = payload;
  try {
    const response = yield Api.post(`${controller}/schedules`, scheduleData);
    // Backend returns { message, status, data: schedule }
    const schedule = response.data.data || response.data;
    yield put(RetailProfileActions.createRetailScheduleSuccess(schedule));

    onDisplayAlert({
      type: 'success',
      title: 'Success',
      description: 'Schedule created successfully!',
    });
  } catch (err) {
    yield put(
      RetailProfileActions.createRetailScheduleFailed(err.message || 'Failed to create schedule'),
    );
    onDisplayAlert({
      type: 'error',
      title: 'Error',
      description: err.response?.data?.message || 'Failed to create schedule',
    });
  }
}

function* fetchGetSchedulesByProfile({ payload }) {
  try {
    const { data } = yield Api.get(`${controller}/${payload.profileId}/schedules`);
    yield put(RetailProfileActions.getSchedulesByProfileSuccess(data));
  } catch (err) {
    yield put(
      RetailProfileActions.getSchedulesByProfileFailed(err.message || 'Failed to fetch schedules'),
    );
  }
}

function* fetchGetSchedulesByDay({ payload }) {
  try {
    const { data } = yield Api.get(`${controller}/${payload.profileId}/schedules/day`, {
      day: payload.day,
    });
    yield put(RetailProfileActions.getSchedulesByDaySuccess(data));
  } catch (err) {
    yield put(
      RetailProfileActions.getSchedulesByDayFailed(err.message || 'Failed to fetch schedules'),
    );
  }
}

function* fetchUpdateRetailSchedule({ payload }) {
  const { id, scheduleData } = payload;
  try {
    const { data } = yield Api.put(`${controller}/schedules/${id}`, scheduleData);
    yield put(RetailProfileActions.updateRetailScheduleSuccess(data));

    onDisplayAlert({
      type: 'success',
      title: 'Success',
      description: 'Schedule updated successfully!',
    });
  } catch (err) {
    yield put(
      RetailProfileActions.updateRetailScheduleFailed(err.message || 'Failed to update schedule'),
    );
    onDisplayAlert({
      type: 'error',
      title: 'Error',
      description: err.response?.data?.message || 'Failed to update schedule',
    });
  }
}

function* fetchDeleteRetailSchedule({ payload }) {
  try {
    yield Api.delete(`${controller}/schedules/${payload.id}`);
    yield put(RetailProfileActions.deleteRetailScheduleSuccess(payload.id));

    onDisplayAlert({
      type: 'success',
      title: 'Success',
      description: 'Schedule deleted successfully!',
    });
  } catch (err) {
    yield put(
      RetailProfileActions.deleteRetailScheduleFailed(err.message || 'Failed to delete schedule'),
    );
    onDisplayAlert({
      type: 'error',
      title: 'Error',
      description: err.response?.data?.message || 'Failed to delete schedule',
    });
  }
}

function* ActionWatcher() {
  yield takeLatest(CREATE_RETAIL_PROFILE, fetchCreateRetailProfile);
  yield takeLatest(GET_RETAIL_PROFILES, fetchGetRetailProfiles);
  yield takeLatest(GET_RETAIL_PROFILE_BY_ID, fetchGetRetailProfileById);
  yield takeLatest(GET_RETAIL_PROFILES_BY_VENUE, fetchGetRetailProfilesByVenue);
  yield takeLatest(UPDATE_RETAIL_PROFILE, fetchUpdateRetailProfile);
  yield takeLatest(DELETE_RETAIL_PROFILE, fetchDeleteRetailProfile);
  yield takeLatest(CREATE_RETAIL_SCHEDULE, fetchCreateRetailSchedule);
  yield takeLatest(GET_SCHEDULES_BY_PROFILE, fetchGetSchedulesByProfile);
  yield takeLatest(GET_SCHEDULES_BY_DAY, fetchGetSchedulesByDay);
  yield takeLatest(UPDATE_RETAIL_SCHEDULE, fetchUpdateRetailSchedule);
  yield takeLatest(DELETE_RETAIL_SCHEDULE, fetchDeleteRetailSchedule);
}

export default function* RetailProfilesSaga() {
  yield all([ActionWatcher()]);
}
